import type { ReactNode } from 'react';

/**
 * The four states every data view must handle — ux-draft.md 0.
 * Not decoration: a screen that renders nothing while loading and
 * nothing when empty teaches the user that the app is broken.
 */

export function Loading({ rows = 5 }: { rows?: number }) {
  return (
    <div aria-busy="true" aria-live="polite">
      <span className="visually-hidden" style={{ position: 'absolute', left: -9999 }}>
        Loading
      </span>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="skel" style={{ height: 52, marginBottom: 8 }} />
      ))}
    </div>
  );
}

export function Empty({
  title,
  body,
  action,
}: {
  title: string;
  body: string;
  action?: ReactNode;
}) {
  return (
    <div className="state">
      <h3>{title}</h3>
      <p>{body}</p>
      {action}
    </div>
  );
}

export function ErrorState({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div className="state">
      <h3>That did not work</h3>
      <p>{message}</p>
      {onRetry && (
        <button className="btn btn-ghost" onClick={onRetry}>
          Try again
        </button>
      )}
    </div>
  );
}

export function Banner({ message }: { message: string }) {
  return (
    <div className="banner err" role="alert">
      {message}
    </div>
  );
}

/* ---------- small display helpers ---------- */

export const CODE_LABEL: Record<string, string> = {
  CODE_1: 'Code 1 · Approved',
  CODE_2: 'Code 2 · Approved as noted',
  CODE_3: 'Code 3 · Revise and resubmit',
  CODE_4: 'Code 4 · Rejected',
};

export function CodeChip({ code }: { code: string }) {
  const advancing = code === 'CODE_1' || code === 'CODE_2';
  return <span className={`chip ${advancing ? 'good' : 'crit'}`}>{CODE_LABEL[code] ?? code}</span>;
}

/**
 * Status always carries a text label. Green and red sit ~4 dE apart
 * under deuteranopia simulation, so colour alone would be unreadable
 * for a red-green colourblind reader.
 */
export function StateChip({ state }: { state: string }) {
  const cls =
    state === 'APPROVED' ? 'good' : state === 'RETURNED' ? 'crit' : state === 'IN_REVIEW' ? 'blue' : '';
  const label = state.replace(/_/g, ' ').toLowerCase();
  return <span className={`chip ${cls}`}>{label.charAt(0).toUpperCase() + label.slice(1)}</span>;
}

export function StageBar({ stage }: { stage: string | null }) {
  if (!stage) return <span className="chip">—</span>;
  return (
    <span className={`stage ${stage}`}>
      <i aria-hidden="true" />
      {stage}
    </span>
  );
}

export function Due({ dueAt }: { dueAt: string | null }) {
  if (!dueAt) return <span className="chip">—</span>;
  const ms = new Date(dueAt).getTime() - Date.now();
  const days = Math.ceil(Math.abs(ms) / 86_400_000);
  if (ms < 0) return <span className="chip crit">Overdue {days}d</span>;
  if (days <= 1) return <span className="chip warn">Due today</span>;
  return <span className="chip">Due {days}d</span>;
}

export function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

const STAGES = ['IFR', 'IFD', 'IFA', 'IFC'] as const;

/**
 * The rail that makes eng. different from pm.: a deliverable runs a
 * full approval cycle per stage, so its position on the ladder is
 * the first thing anyone wants to see.
 *
 * Filled dots use the ordinal ramp, not the series palette. Stage is
 * a sequence, and a sequence is drawn in one hue from light to dark.
 */
export function StageRail({ current }: { current: string | null }) {
  const idx = current ? STAGES.indexOf(current as (typeof STAGES)[number]) : -1;
  return (
    <div className="rail" role="img" aria-label={`Stage ${current ?? 'not set'} of IFR, IFD, IFA, IFC`}>
      {STAGES.map((s, i) => (
        <span key={s} style={{ display: 'contents' }}>
          {i > 0 && <span className={`rail-line ${i <= idx ? 'done' : ''}`} />}
          <span className="rail-step">
            <span className={`rail-dot ${i < idx ? 'done' : i === idx ? 'now' : ''}`} />
            {s}
          </span>
        </span>
      ))}
    </div>
  );
}
